import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Prompt } from "react-router";
import { Button, Space } from "antd";
import { every, isNil, map, trim } from "lodash";
import { LEAVE_CONFIRMATION } from "constants/common";
import { aiccUiElementTranslationApi } from "services/aiccLearningAssets";
import { AiccUiElementTranslationRow } from "types/model/aiccLearningAssets";
import getColumns from "./columnConfig";
import { UI_ELEMENT_FIELD_GUIDE, UI_ELEMENT_FIELD_GUIDE_INTRO, UI_ELEMENT_TRANSLATION_TITLE } from "./configs";
import LearningAssetPage from "../LearningAssetPage";
import { useLearningAssetVersions } from "../hooks/useLearningAssetVersions";

function UiElementTranslation() {
  const state = useLearningAssetVersions(aiccUiElementTranslationApi.fetchVersions);
  const { items, languageId, groupStatus, isEditable, isSaving, runWrite } = state;

  const [draftRows, setDraftRows] = useState<AiccUiElementTranslationRow[]>();
  const [showErrors, setShowErrors] = useState(false);

  const isEditing = !isNil(draftRows);
  const rows = draftRows ?? items;

  const cancelEdit = useCallback(() => {
    setDraftRows(undefined);
    setShowErrors(false);
  }, []);

  useEffect(() => {
    cancelEdit();
  }, [cancelEdit, groupStatus, languageId]);

  const saveEdit = useCallback(() => {
    if (isNil(draftRows)) {
      return;
    }
    if (!every(draftRows, ({ translation }) => !!trim(translation))) {
      setShowErrors(true);
      return;
    }
    runWrite(
      (id) => aiccUiElementTranslationApi.update(id, draftRows),
      `Updated ${UI_ELEMENT_TRANSLATION_TITLE} successfully.`,
      cancelEdit
    );
  }, [cancelEdit, draftRows, runWrite]);

  const changeTranslation = useCallback((element: string, translation: string) => {
    setDraftRows((currentRows) => map(currentRows, (row) => (row.id === element ? { ...row, translation } : row)));
  }, []);

  const columns = useMemo(() => getColumns({ isEditing, showErrors, onTranslationChange: changeTranslation }), [
    changeTranslation,
    isEditing,
    showErrors,
  ]);

  return (
    <>
      <Prompt when={isEditing || isSaving} message={LEAVE_CONFIRMATION} />
      <LearningAssetPage<AiccUiElementTranslationRow>
        title={UI_ELEMENT_TRANSLATION_TITLE}
        fieldGuideIntro={UI_ELEMENT_FIELD_GUIDE_INTRO}
        fieldGuide={UI_ELEMENT_FIELD_GUIDE}
        columns={columns}
        state={{ ...state, items: rows }}
        isEditing={isEditing}
        toolbarActions={
          isEditable &&
          (isEditing ? (
            <Space size={12}>
              <Button shape="round" disabled={isSaving} onClick={cancelEdit}>
                Cancel
              </Button>
              <Button type="primary" shape="round" disabled={isSaving} onClick={saveEdit}>
                Save
              </Button>
            </Space>
          ) : (
            <Button type="primary" shape="round" disabled={isNil(languageId)} onClick={() => setDraftRows(items)}>
              Edit
            </Button>
          ))
        }
      />
    </>
  );
}

export default UiElementTranslation;
